import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  AnnotationData,
  AnnotationDelete,
  AnnotationState,
  MagicWand,
  DeleteAllAnnotation,
  LabelProperty,
  AnnotationLabelDetails,
  labelAssistedAnnotationsData,
} from "../../types/interface/Annotation.interface";
import AxiosInstance from "../../Service/AxiosInstance";
import { Image } from "./ProjectImagesUploadedSlice";
import { updateImageInList } from "./ProjectImagesUploadedSlice";

const initialState: AnnotationState = {
  loading: false,
  error: null,
  success: false,
  labelDetails: [],
  magicWandData: {
    imageId: "",
    projectId: "",
    contours: [],
  },
};

export const fetchLabelDetails = createAsyncThunk(
  "projectAnnotation/GetLabelsDetails",
  async (imageId: string) => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/Annotations/GetLabelsDetails?imageId=${imageId}`
    );
    return response.data;
  }
);

export const saveImageAnnotation = createAsyncThunk(
  "projectAnnotation/saveImageAnnotation",
  async (data: AnnotationData, { rejectWithValue, dispatch }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Annotations/Add?projectImageId=${data.projectImageId}`,
        data
      );
      // const updatedImage = response.data.value.imageUrlModel;

      // dispatch(
      //   updateImageInList({
      //     id: updatedImage[0].id,
      //     updatedImage: updatedImage[0],
      //   })
      // );

      return response.data;
    } catch (error: any) {
      // Handle errors
      return rejectWithValue(error.response.data);
    }
  }
);

// Update Annotation Thunk
export const updateAnnotation = createAsyncThunk(
  "annotations/update",
  async (data: AnnotationData, { rejectWithValue, dispatch }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Annotations/Update?projectImageId=${data.projectImageId}&annotationId=${data.annotationId}`,
        data
      );

      // const updatedImage = response.data.value.imageUrlModel;

      // dispatch(
      //   updateImageInList({
      //     id: updatedImage[0].id,
      //     updatedImage: updatedImage[0],
      //   })
      // );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteAnnotation = createAsyncThunk(
  "annotations/delete",
  async (data: AnnotationDelete, { rejectWithValue, dispatch }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Annotations/Delete?projectImageId=${data.projectImageId}&annotationId=${data.annotationId}`
      );

      // const updatedImage = response.data.value.imageUrlModel;

      // dispatch(
      //   updateImageInList({
      //     id: updatedImage[0].id,
      //     updatedImage: updatedImage[0],
      //   })
      // );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addAnnotationMagicWand = createAsyncThunk(
  "annotations/addMagicWand",
  async (data: MagicWand, { rejectWithValue, dispatch }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Annotations/AddMagicwand?projectImageId=${data.projectImageId}`,
        data
      );

      return response.data;
    } catch (error: any) {
      throw error;
      //return rejectWithValue(error.response.data);
    }
  }
);

export const deleteAllAnnotations = createAsyncThunk(
  "projectAnnotation/deleteAllAnnotations",
  async (data: DeleteAllAnnotation, { rejectWithValue, dispatch }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Annotations/DeleteAll`,
        data
      );

      if (response.data) {
        const updatedImage = response.data;

        dispatch(
          updateImageInList({
            id: updatedImage.id,
            updatedImage: updatedImage,
          })
        );
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const labelAssistedAnnotations = createAsyncThunk(
  "annotations/labelAssistedAnnotations",
  async (data: labelAssistedAnnotationsData, { rejectWithValue, dispatch }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/LabelMapping/GetAllResults`,
        data
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const saveBulkImageAnnotation = createAsyncThunk(
  "projectAnnotation/saveBulkImageAnnotation",
  async (data: AnnotationData[], { rejectWithValue, dispatch }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Annotations/AddBulk?projectImageId=${data[0].projectImageId}`,
        data
      );

      return response.data;
    } catch (error: any) {
      // Handle errors
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchModelDetails = createAsyncThunk(
  "projectAnnotation/modelDetails",
  async (id: string) => {
    const response = await AxiosInstance.get(
      `${process.env.REACT_APP_SERVER_BASEURL}/api/LabelMapping/GetModelId?projectId=${id}`
    );
    return response.data;
  }
);

const projectAnnotationSlice = createSlice({
  name: "annotations",
  initialState,
  reducers: {
    selectAllForColor: (
      state,
      action: PayloadAction<{ index: number; isChecked: boolean }>
    ) => {
      const { index, isChecked } = action.payload;

      state.labelDetails.forEach((group, i) => {
        group.labelProperties.forEach((label) => {
          label.isChecked = i === index ? isChecked : false;
        });
      });
    },
    updateLabelDetails: (state, action: PayloadAction<Image>) => {
      state.labelDetails = [];
      const updatedImage = action.payload;

      const data = updatedImage.annotations;

      const grouped = data.reduce((acc, curr) => {
        const existingGroup = acc.find((g: any) => g.color === curr.labelColor);
        const labelEntry = {
          labelId: curr.lableId,
          labelName: curr.projectLabel,
          annotationId: curr.annotationId,
          isChecked: false,
        };
        if (existingGroup) {
          existingGroup.labelProperties.push(labelEntry);
        } else {
          acc.push({
            color: curr.labelColor,
            labelProperties: [labelEntry],
          });
        }
        return acc;
      }, []);

      state.labelDetails = grouped;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabelDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchLabelDetails.fulfilled, (state, action) => {
        state.labelDetails = [];
        const result: AnnotationLabelDetails[] = [];
        const data = action.payload;

        let index = 0;

        data.forEach((colorGroup: any) => {
          const labelGroups: { [key: string]: LabelProperty[] } = {};

          colorGroup.labelProperties.forEach((label: any) => {
            if (!labelGroups[label.labelId]) {
              labelGroups[label.labelId] = [];
            }
            labelGroups[label.labelId].push(label);
          });

          Object.values(labelGroups).forEach((group) => {
            result.push({
              index: index++,
              color: colorGroup.color,
              labelProperties: group,
            });
          });
        });

        state.labelDetails = result;
        state.loading = false;
        state.success = true;
      })
      .addCase(fetchLabelDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    builder
      .addCase(saveImageAnnotation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(saveImageAnnotation.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
      })

      .addCase(saveImageAnnotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    builder
      .addCase(updateAnnotation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateAnnotation.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateAnnotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    builder
      .addCase(deleteAnnotation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteAnnotation.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteAnnotation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    builder
      .addCase(deleteAllAnnotations.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteAllAnnotations.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteAllAnnotations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });

    builder
      .addCase(addAnnotationMagicWand.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addAnnotationMagicWand.fulfilled, (state, action) => {
        state.magicWandData = action.payload;
        state.loading = false;
        state.success = true;
      })
      .addCase(addAnnotationMagicWand.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  },
});

export const { selectAllForColor, updateLabelDetails } =
  projectAnnotationSlice.actions;

export default projectAnnotationSlice.reducer;
